// Batch 134 — the shared shell and prose primitives for the two legal pages.
//
// WHY ITS OWN SHELL. Landing.jsx's Nav, Footer and StickyMobileCta are
// module-private consts, not exports. Reusing them would mean refactoring the
// marketing page in order to publish a legal one, which is the wrong direction
// of risk for a page whose whole job is to be boring and always available.
//
// WHY THIS RENDERS THE SAME ON BOTH HOSTS. The marketing page hardcodes hex
// values and the app uses tailwind tokens, and they are THE SAME COLOURS:
// #0D0D0F wingman-bg, #2A2A35 wingman-border, #C9A84C wingman-gold,
// #F0F0F0 wingman-text, #7A7A8C wingman-muted. Written in tokens, this looks
// native wherever it is served, with no palette fork.
//
// Logo comes from marketing/ui, whose only import is ./motion — a local CSS
// shim, not framer-motion (see its header: the shim exists to keep ~183 kB out
// of the marketing chunk). So importing it here costs nothing.
import { Logo } from "@/marketing/ui";
import { LAST_UPDATED } from "./legalMeta";

// An id derived from the heading text, so /privacy#your-rights works. Support
// replies, app-store forms and payment reviews all link to a single section.
export const slug = (text) =>
  String(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const H2 = ({ children }) => (
  <h2 id={slug(children)} className="mt-10 font-bebas text-2xl tracking-wide text-wingman-text">
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 id={slug(children)} className="mt-6 text-base font-semibold text-wingman-text">
    {children}
  </h3>
);

export const P = ({ children }) => (
  <p className="mt-3 text-sm leading-relaxed text-wingman-muted">{children}</p>
);

export const B = ({ children }) => (
  <strong className="font-semibold text-wingman-text">{children}</strong>
);

export const UL = ({ children }) => (
  <ul className="mt-3 space-y-2 text-sm leading-relaxed text-wingman-muted">{children}</ul>
);

export const LI = ({ children }) => (
  <li className="flex gap-2.5">
    <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-wingman-gold" />
    <span>{children}</span>
  </li>
);

export const Rule = () => <hr className="mt-10 border-wingman-border" />;

// The two tables in privacy.md are GFM. Rendered as paragraphs they read as a
// wall of pipe characters, so they are real tables, and they scroll inside
// their own container rather than making the page scroll sideways on a phone.
export const Table = ({ head, rows }) => (
  <div className="mt-4 overflow-x-auto">
    <table className="w-full min-w-[28rem] border-collapse text-left text-sm">
      <thead>
        <tr className="border-b border-wingman-border">
          {head.map((h) => (
            <th key={h} className="py-2 pr-4 font-semibold text-wingman-text">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-wingman-border/60 align-top">
            {row.map((cell, j) => (
              <td key={j} className="py-2.5 pr-4 text-wingman-muted">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Mail = () => (
  <a href="mailto:sparkcue0@gmail.com" className="text-wingman-gold underline hover:opacity-80">
    sparkcue0@gmail.com
  </a>
);

export default function LegalPage({ title, testId, children }) {
  return (
    <div className="min-h-screen bg-wingman-bg text-wingman-text" data-testid={testId}>
      <header className="border-b border-wingman-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
          <Logo className="h-7" />
          {/* A RELATIVE "/" on purpose. It means home for whichever host the
              reader is on: the application on app.sparkcue.co, the marketing
              page everywhere else. getMarketingUrl() would throw a reader who
              opened this from inside the app out to the marketing site rather
              than back to their conversation. */}
          <a href="/" data-testid="legal-back"
             className="text-sm text-wingman-muted transition-colors hover:text-wingman-text">
            Back
          </a>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 pb-24 pt-10">
        <h1 className="font-bebas text-4xl leading-tight tracking-wide text-wingman-text">
          {title}
        </h1>
        <p className="mt-2 text-xs uppercase tracking-wider text-wingman-muted"
           data-testid="legal-last-updated">
          Last updated: {LAST_UPDATED}
        </p>
        {children}
      </main>
    </div>
  );
}
